import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  List,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Spin,
  Empty,
  Drawer,
  message,
  Dropdown,
  AutoComplete,
  Tag,
} from 'antd';
import {
  SearchOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  PlusOutlined,
  EyeOutlined,
  GlobalOutlined,
  MessageOutlined,
  CompassOutlined,
  AimOutlined,
  TagOutlined,
  StarOutlined,
  PhoneOutlined as PhoneIcon,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getNearbyApartments, convertApartmentToLead } from '../../api/apartments';
import { API_STALE_TIME_MS } from '../../constants/api';
import type { ApartmentFromPlaces } from '../../types/apartments';
import GoogleMapsView from './GoogleMapsView';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleMapsService } from '../../services/googleMaps';
import { 
  addProspectTag, 
  removeProspectTag, 
  updateProspectTag, 
  getAllProspectTags 
} from '../../api/prospectTags';
import type { ProspectTag, ProspectTagType } from '../../types/common';

const { Title, Text } = Typography;
const { Option } = Select;

interface DiscoveryTabProps {
  onViewLead: (lead: any) => void;
}

// Using the imported type instead of local interface

const DiscoveryTab: React.FC<DiscoveryTabProps> = ({
  onViewLead,
}) => {
  const { t } = useTranslation(['common', 'sales']);
  const { firestoreUser } = useAuth();
  
  const [selectedApartment, setSelectedApartment] = useState<ApartmentFromPlaces | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [searchLocation, setSearchLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapView, setMapView] = useState<'map' | 'list' | 'split'>('split');
  const [searchType, setSearchType] = useState<string>('Condominio');
  const [customSearchTerm, setCustomSearchTerm] = useState<string>('');
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [manualLocation, setManualLocation] = useState<string>('');
  const [locationSuggestions, setLocationSuggestions] = useState<Array<{ value: string; label: string; placeId: string; placeData: any }>>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [googleMapsService, setGoogleMapsService] = useState<GoogleMapsService | null>(null);

  // Prospect tagging state
  const [prospectTags, setProspectTags] = useState<ProspectTag[]>([]);
  const [filterType, setFilterType] = useState<'all' | 'exclude-tagged' | 'by-tag'>('exclude-tagged');
  const [selectedTagFilter, setSelectedTagFilter] = useState<ProspectTagType | null>(null);


  // Search options for dropdown
  const searchOptions = [
    { label: 'Condominio', value: 'Condominio' },
    { label: 'Departamento', value: 'Departamento' },
    { label: 'Edificio', value: 'Edificio' },
    { label: 'Apartamento', value: 'Apartamento' },
    { label: 'Apartment', value: 'Apartment' },
    { label: 'All', value: 'All' },
    { label: 'Other', value: 'Other' },
  ];

  // Initialize Google Maps service
  useEffect(() => {
    const initGoogleMaps = async () => {
      try {
        const service = GoogleMapsService.getInstance();
        await service.initialize();
        setGoogleMapsService(service);
      } catch (error) {
        console.error('Failed to initialize Google Maps service:', error);
        message.warning('Google Maps service not available. You can still enter locations manually.');
      }
    };

    initGoogleMaps();
  }, []);

  // Load prospect tags for the current user
  useEffect(() => {
    const loadProspectTags = async () => {
      if (!firestoreUser?.id) return;
      
      try {
        const tags = await getAllProspectTags();
        setProspectTags(tags);
      } catch (error) {
        console.error('Error loading prospect tags:', error);
        message.error('Failed to load prospect tags');
      }
    };

    loadProspectTags();
  }, [firestoreUser?.id]);

  // Get search terms based on selection
  const getSearchTerms = useCallback(() => {
    if (searchType === 'All') {
      return ['Condominio', 'Departamento', 'Edificio', 'Apartamento', 'Apartment'];
    } else if (searchType === 'Other') {
      return customSearchTerm.trim() ? [customSearchTerm.trim()] : [];
    } else {
      return [searchType];
    }
  }, [searchType, customSearchTerm]);

  // Request location permission
  const requestLocationPermission = () => {
    if (!navigator.geolocation) {
      message.error('Geolocation is not supported by this browser');
      return;
    }

    setIsRequestingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(location);
        setSearchLocation(location);
        setIsRequestingLocation(false);
        message.success('Location access granted!');
      },
      (error) => {
        console.error('Error getting location:', error);
        setIsRequestingLocation(false);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message.error('Location access denied. Please enable location permissions in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            message.error('Location information is unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            message.error('Location request timed out. Please try again.');
            break;
          default:
            message.error('An error occurred while getting your location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  // Handle location search with autocomplete
  const handleLocationSearch = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim() || searchTerm.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    if (!googleMapsService) {
      // Fallback to manual geocoding if Google Maps service is not available
      return;
    }

    setIsLoadingSuggestions(true);
    
    try {
      // Use the modern Places API through our service
      const places = await googleMapsService.searchPlacesByText(searchTerm, { lat: 0, lng: 0 });
      
      if (places && places.length > 0) {
        const suggestions = places.slice(0, 5).map(place => {
          return {
            value: place.displayName || place.formattedAddress || 'Unknown location',
            label: place.displayName || place.formattedAddress || 'Unknown location',
            placeId: place.id || '',
            // Store the full place data for later use
            placeData: place,
          };
        });
        setLocationSuggestions(suggestions);
      } else {
        setLocationSuggestions([]);
      }
    } catch (error) {
      console.error('Error getting location suggestions:', error);
      // Fallback to manual geocoding
      setLocationSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [googleMapsService]);

  // Handle location selection from autocomplete
  const handleLocationSelect = useCallback(async (value: string) => {
    const selectedSuggestion = locationSuggestions.find(s => s.value === value);
    if (!selectedSuggestion) return;

    try {
      // Check if we have location data directly from the Places API
      if (selectedSuggestion.placeData && selectedSuggestion.placeData.location) {
        // The lat and lng are functions in the modern Places API, so we need to call them
        const latFunction = selectedSuggestion.placeData.location.lat;
        const lngFunction = selectedSuggestion.placeData.location.lng;
        
        if (typeof latFunction === 'function' && typeof lngFunction === 'function') {
          const latitude = latFunction();
          const longitude = lngFunction();
          
          if (latitude !== undefined && longitude !== undefined) {
            const location = { lat: latitude, lng: longitude };
            
            setSearchLocation(location);
            setManualLocation(selectedSuggestion.value);
            setLocationSuggestions([]); // Clear suggestions
            message.success(`Location set to: ${selectedSuggestion.value}`);
            return;
          }
        } else {
          // Fallback: try to access as direct properties
          const { latitude, longitude } = selectedSuggestion.placeData.location;
          
          if (latitude && longitude) {
            const location = { lat: latitude, lng: longitude };
            
            setSearchLocation(location);
            setManualLocation(selectedSuggestion.value);
            setLocationSuggestions([]); // Clear suggestions
            message.success(`Location set to: ${selectedSuggestion.value}`);
            return;
          }
        }
      }

      // Try to get detailed place information using Places API
      if (googleMapsService && selectedSuggestion.placeId) {
        try {
          // Use the Places API to get detailed information about the selected place
          const placeDetails = await googleMapsService.getPlaceDetails(selectedSuggestion.placeId);
          
          if (placeDetails && placeDetails.location) {
            const { latitude, longitude } = placeDetails.location;
            if (latitude && longitude) {
              const location = { lat: latitude, lng: longitude };
              
              setSearchLocation(location);
              setManualLocation(selectedSuggestion.value);
              setLocationSuggestions([]); // Clear suggestions
              message.success(`Location set to: ${selectedSuggestion.value}`);
              return;
            }
          }
        } catch (placesError) {
          // Silently fall back to geocoding API
        }
      }

      // Fallback: Use Google Geocoding API to get coordinates for the selected place
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${selectedSuggestion.placeId}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
      );
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        const location = { lat, lng };
        setSearchLocation(location);
        setManualLocation(selectedSuggestion.value);
        setLocationSuggestions([]); // Clear suggestions
        message.success(`Location set to: ${selectedSuggestion.value}`);
      } else {
        message.error('Location not found. Please try a different address.');
      }
    } catch (error) {
      console.error('Error geocoding selected location:', error);
      message.error('Error setting location. Please try again.');
    }
  }, [locationSuggestions, googleMapsService]);



  // Prospect tagging functions
  const handleAddTag = async (apartment: ApartmentFromPlaces, tagType: ProspectTagType) => {
    if (!firestoreUser?.id) return;
    
    try {
      const apartmentInfo = {
        name: apartment.name,
        address: apartment.address || '',
        phone: apartment.phone || apartment.nationalPhone || '',
        website: apartment.website,
        rating: apartment.rating,
        totalRatings: apartment.totalRatings,
      };

      await addProspectTag(firestoreUser.id, apartment.placeId, tagType, apartmentInfo);
      // Refresh tags
      const tags = await getAllProspectTags();
      setProspectTags(tags);
      message.success(`Tagged as ${tagType}`);
    } catch (error) {
      console.error('Error adding tag:', error);
      message.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (placeId: string) => {
    if (!firestoreUser?.id) return;
    
    try {
      await removeProspectTag(firestoreUser.id, placeId);
      // Refresh tags
      const tags = await getAllProspectTags();
      setProspectTags(tags);
      message.success('Tag removed');
    } catch (error) {
      console.error('Error removing tag:', error);
      message.error('Failed to remove tag');
    }
  };

  const handleUpdateTag = async (placeId: string, newTagType: ProspectTagType) => {
    if (!firestoreUser?.id) return;
    
    try {
      await updateProspectTag(firestoreUser.id, placeId, newTagType);
      // Refresh tags
      const tags = await getAllProspectTags();
      setProspectTags(tags);
      message.success(`Tag updated to ${newTagType}`);
    } catch (error) {
      console.error('Error updating tag:', error);
      message.error('Failed to update tag');
    }
  };

  // Handle location marker drag
  const handleLocationChange = useCallback((newLocation: { lat: number; lng: number }) => {
    setSearchLocation(newLocation);
  }, []);

  // Fetch nearby apartments - This will make fresh API calls when searchType or customSearchTerm changes
  const {
    data: apartments = [],
    isLoading,
    error,
  } = useQuery<ApartmentFromPlaces[]>({
    queryKey: ['nearby-apartments', searchLocation?.lat, searchLocation?.lng, searchType, customSearchTerm],
    queryFn: () => {
      return getNearbyApartments(searchLocation!, getSearchTerms());
    },
    enabled: !!searchLocation && getSearchTerms().length > 0,
    staleTime: API_STALE_TIME_MS,
  });

  // Sort apartments to prioritize those with contact information
  const filteredApartments = useMemo(() => {
    let filtered = [...apartments];
    
    // Apply prospect tag filtering
    switch (filterType) {
      case 'exclude-tagged':
        // Filter out all tagged places
        filtered = filtered.filter(apt => 
          !prospectTags.some(tag => tag.placeId === apt.placeId)
        );
        break;
      case 'by-tag':
        // Show only places with specific tag
        if (selectedTagFilter) {
          filtered = filtered.filter(apt => 
            prospectTags.some(tag => 
              tag.placeId === apt.placeId && tag.tagType === selectedTagFilter
            )
          );
        }
        break;
      case 'all':
      default:
        // Show all places
        break;
    }
    
    // Sort apartments to prioritize those with contact information
    return filtered.sort((a, b) => {
      // Check if apartment has contact info (phone or website)
      const aHasContact = !!(a.phone || a.website);
      const bHasContact = !!(b.phone || b.website);
      
      // If one has contact info and the other doesn't, prioritize the one with contact
      if (aHasContact && !bHasContact) return -1;
      if (!aHasContact && bHasContact) return 1;
      
      // If both have contact info or both don't, sort by rating (highest first)
      if (a.rating && b.rating && a.rating !== b.rating) {
        return b.rating - a.rating;
      }
      
      // If ratings are equal or missing, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  }, [apartments, prospectTags, filterType, selectedTagFilter]);



  const handleApartmentClick = useCallback((apartment: ApartmentFromPlaces) => {
    setSelectedApartment(apartment);
  }, []);

  const handleCreateLead = useCallback(async (apartment: ApartmentFromPlaces) => {
    try {
      await convertApartmentToLead(apartment);
      message.success(t('sales:discovery.leadCreatedSuccess'));
      // Optionally navigate to the new lead
      // navigate(`/sales/leads/${leadId}`);
    } catch (error) {
      message.error(t('sales:discovery.leadCreationFailed'));
      console.error('Error creating lead:', error);
    }
  }, [t]);

  // WhatsApp message templates - Multilingual support
  const getWhatsAppTemplates = useCallback(() => {
    const userLanguage = firestoreUser?.language || 'en';
    
    const templates = {
      en: [
        {
          key: 'welcome',
          label: 'Welcome Message',
          message: `Hi,
I am trying to talk to someone who manages {PROPERTY_NAME}, my name is {USER_NAME}. Thank you.`
        },
        {
          key: 'followup',
          label: 'Follow Up',
          message: `Hi! I wanted to follow up on our previous conversation about your property. Do you have any questions or would you like to schedule a consultation?`
        },
        {
          key: 'pricing',
          label: 'Pricing Information',
          message: `Hi! I'd be happy to provide you with a free property valuation and market analysis. Would you like me to prepare a detailed report for your property?`
        },
        {
          key: 'link',
          label: 'Property Link',
          message: `Hi! I found your property listing and would love to discuss it further. Here's a link to our services: [Your Website]. Would you like to learn more?`
        },
        {
          key: 'custom',
          label: 'Custom Message',
          message: `Hi! I'm interested in your property and would love to discuss potential opportunities.`
        }
      ],
      es: [
        {
          key: 'welcome',
          label: 'Mensaje de Bienvenida',
          message: `Hola,
estoy tratando de hablar con alguien que administre {PROPERTY_NAME}, mi nombre es {USER_NAME}. Gracias.`
        },
        {
          key: 'followup',
          label: 'Seguimiento',
          message: `¡Hola! Quería hacer seguimiento de nuestra conversación anterior sobre tu propiedad. ¿Tienes alguna pregunta o te gustaría programar una consulta?`
        },
        {
          key: 'pricing',
          label: 'Información de Precios',
          message: `¡Hola! Me encantaría proporcionarte una valuación gratuita de la propiedad y análisis de mercado. ¿Te gustaría que prepare un reporte detallado para tu propiedad?`
        },
        {
          key: 'link',
          label: 'Enlace de Propiedad',
          message: `¡Hola! Encontré el listado de tu propiedad y me encantaría discutir más detalles. Aquí tienes un enlace a nuestros servicios: [Tu Sitio Web]. ¿Te gustaría saber más?`
        },
        {
          key: 'custom',
          label: 'Mensaje Personalizado',
          message: `¡Hola! Estoy interesado en tu propiedad y me encantaría discutir oportunidades potenciales.`
        }
      ],
      pt: [
        {
          key: 'welcome',
          label: 'Mensagem de Boas-vindas',
          message: `Olá,
estou tentando falar com alguém que administra {PROPERTY_NAME}, meu nome é {USER_NAME}. Obrigado.`
        },
        {
          key: 'followup',
          label: 'Acompanhamento',
          message: `Olá! Queria acompanhar nossa conversa anterior sobre sua propriedade. Você tem alguma pergunta ou gostaria de agendar uma consulta?`
        },
        {
          key: 'pricing',
          label: 'Informações de Preços',
          message: `Olá! Adoraria fornecer uma avaliação gratuita da propriedade e análise de mercado. Você gostaria que eu prepare um relatório detalhado para sua propriedade?`
        },
        {
          key: 'link',
          label: 'Link da Propriedade',
          message: `Olá! Encontrei o anúncio da sua propriedade e adoraria discutir mais detalhes. Aqui está um link para nossos serviços: [Seu Site]. Você gostaria de saber mais?`
        },
        {
          key: 'custom',
          label: 'Mensagem Personalizada',
          message: `Olá! Estou interessado na sua propriedade e adoraria discutir oportunidades potenciais.`
        }
      ]
    };
    
    return templates[userLanguage as keyof typeof templates] || templates.en;
  }, [firestoreUser?.language]);

  // Handle WhatsApp message
  const handleWhatsAppMessage = useCallback((phoneNumber: string, templateKey: string, apartmentName?: string) => {
    const templates = getWhatsAppTemplates();
    const template = templates.find(t => t.key === templateKey);
    if (!template) return;

    let message = template.message;
    
    // Replace placeholders in welcome message
    if (templateKey === 'welcome') {
      const userName = firestoreUser?.displayName || 'Sales Manager';
      
      message = message
        .replace('{PROPERTY_NAME}', apartmentName || 'this property')
        .replace('{USER_NAME}', userName);
    }
    
    // If it's a custom message, allow user to edit
    if (templateKey === 'custom') {
      const userLanguage = firestoreUser?.language || 'en';
      const promptTexts = {
        en: 'Enter your custom message:',
        es: 'Ingresa tu mensaje personalizado:',
        pt: 'Digite sua mensagem personalizada:'
      };
      const promptText = promptTexts[userLanguage as keyof typeof promptTexts] || promptTexts.en;
      message = prompt(promptText, template.message) || template.message;
    }

    // Format phone number for WhatsApp
    let cleanPhone = phoneNumber.replace(/\D/g, ''); // Remove non-digits
    
    // Add country code if missing (assuming US/Canada +1 if no country code)
    if (cleanPhone.length === 10) {
      cleanPhone = '1' + cleanPhone; // Add US country code
    } else if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
      // Already has US country code
    } else if (cleanPhone.length === 9 && cleanPhone.startsWith('9')) {
      cleanPhone = '56' + cleanPhone; // Add Chile country code
    } else if (cleanPhone.length === 8 && cleanPhone.startsWith('2')) {
      cleanPhone = '56' + cleanPhone; // Add Chile country code for mobile
    }
    

    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
  }, [firestoreUser?.displayName, getWhatsAppTemplates]);



  const renderApartmentCard = (apartment: ApartmentFromPlaces) => (
    <Card
      key={apartment.id}
      hoverable
      style={{ 
        marginBottom: 16, 
        cursor: 'pointer',
        overflow: 'hidden'
      }}
      onClick={() => handleApartmentClick(apartment)}
      actions={[
        <Button
          key="view"
          type="text"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onViewLead({ id: apartment.id, name: apartment.name });
          }}
        >
          {t('common:view')}
        </Button>,
        <Button
          key="add"
          type="primary"
          icon={<PlusOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleCreateLead(apartment);
          }}
        >
          {t('sales:discovery.createLead')}
        </Button>,
        // Prospect tagging dropdown
        <Dropdown
          key="tag"
          menu={{
            items: [
              // Show current tag if exists
              ...(prospectTags.find(tag => tag.placeId === apartment.placeId) ? [
                {
                  key: 'current-tag',
                  label: `Current: ${prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType}`,
                  disabled: true,
                  icon: <TagOutlined />
                },
                { type: 'divider' as const }
              ] : []),
              // Tag options
              {
                key: 'no-whatsapp',
                label: t('sales:discovery.prospectTagging.noWhatsApp'),
                icon: <PhoneIcon style={{ color: '#ff4d4f' }} />,
                onClick: () => handleAddTag(apartment, 'no-whatsapp')
              },
              {
                key: 'already-contacted',
                label: t('sales:discovery.prospectTagging.alreadyContacted'),
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                onClick: () => handleAddTag(apartment, 'already-contacted')
              },
              {
                key: 'favourite',
                label: 'favourite',
                icon: <StarOutlined style={{ color: '#faad14' }} />,
                onClick: () => handleAddTag(apartment, 'favourite')
              },
              // Change tag options if current tag exists
              ...(prospectTags.find(tag => tag.placeId === apartment.placeId) ? [
                { type: 'divider' as const },
                {
                  key: 'change-to-no-whatsapp',
                  label: 'Change to: No WhatsApp',
                  icon: <PhoneIcon style={{ color: '#ff4d4f' }} />,
                  onClick: () => handleUpdateTag(apartment.placeId, 'no-whatsapp')
                },
                {
                  key: 'change-to-already-contacted',
                  label: 'Change to: Already Contacted',
                  icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                  onClick: () => handleUpdateTag(apartment.placeId, 'already-contacted')
                },
                {
                  key: 'change-to-favourite',
                  label: 'Change to: Favourite',
                  icon: <StarOutlined style={{ color: '#faad14' }} />,
                  onClick: () => handleUpdateTag(apartment.placeId, 'favourite')
                },
                { type: 'divider' as const }
              ] : []),
              // Remove tag option if exists
              ...(prospectTags.find(tag => tag.placeId === apartment.placeId) ? [
                {
                  key: 'remove-tag',
                  label: 'Remove Tag',
                  icon: <TagOutlined />,
                  onClick: () => handleRemoveTag(apartment.placeId)
                }
              ] : [])
            ]
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<TagOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: prospectTags.find(tag => tag.placeId === apartment.placeId) ? '#1890ff' : undefined
            }}
          >
            Tag
          </Button>
        </Dropdown>
      ]}
    >
      <div style={{ 
        display: 'flex', 
        gap: 16,
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
      }}>
        {/* Photo Section */}
        {apartment.photos && apartment.photos.length > 0 && (
          <div style={{ 
            width: window.innerWidth <= 768 ? '100%' : 120, 
            height: window.innerWidth <= 768 ? 120 : 90, 
            flexShrink: 0 
          }}>
            <img
              src={`https://places.googleapis.com/v1/${apartment.photos[0].name}/media?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&maxWidthPx=${window.innerWidth <= 768 ? 300 : 120}&maxHeightPx=${window.innerWidth <= 768 ? 120 : 90}`}
              alt={apartment.name}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                borderRadius: 6
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
            {apartment.name}
          </Title>
          
          {/* Prospect Tag Indicator */}
          {prospectTags.find(tag => tag.placeId === apartment.placeId) && (
            <div style={{ marginBottom: 8 }}>
              <Tag
                color={
                  prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'no-whatsapp' ? 'red' :
                  prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'already-contacted' ? 'green' :
                  prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'favourite' ? 'gold' : 'blue'
                }
                icon={
                  prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'no-whatsapp' ? <PhoneIcon /> :
                  prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'already-contacted' ? <CheckCircleOutlined /> :
                  prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'favourite' ? <StarOutlined /> : <TagOutlined />
                }
              >
                {prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'no-whatsapp' ? t('sales:discovery.prospectTagging.noWhatsApp') :
                 prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'already-contacted' ? t('sales:discovery.prospectTagging.alreadyContacted') :
                 prospectTags.find(tag => tag.placeId === apartment.placeId)?.tagType === 'favourite' ? t('sales:discovery.prospectTagging.favourite') : ''}
              </Tag>
            </div>
          )}
          
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text type="secondary">
              <EnvironmentOutlined /> {apartment.address}, {apartment.city}
            </Text>
            <Text>
              Rating: ⭐ {apartment.rating || 'N/A'} ({apartment.totalRatings || 0} reviews)
            </Text>
            {apartment.phone && (
              <div style={{ 
                display: 'flex', 
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                gap: 8,
                alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center'
              }}>
                <Text style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  <PhoneOutlined /> {apartment.phone}
                </Text>
                                  <Dropdown
                    menu={{
                      items: getWhatsAppTemplates().map(template => ({
                        key: template.key,
                        label: template.label,
                        icon: <MessageOutlined />,
                        onClick: () => handleWhatsAppMessage(apartment.phone!, template.key, apartment.name)
                      }))
                    }}
                    placement={window.innerWidth <= 768 ? "bottomLeft" : "bottomRight"}
                    trigger={['click']}
                  >
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<MessageOutlined />}
                      style={{ 
                        padding: '0 4px',
                        flexShrink: 0
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      WhatsApp
                    </Button>
                  </Dropdown>
              </div>
            )}

            {apartment.website && (
              <Text>
                <a href={apartment.website} target="_blank" rel="noopener noreferrer">
                  <GlobalOutlined /> {t('sales:discovery.website')}
                </a>
              </Text>
            )}
          </Space>
        </div>
      </div>
    </Card>
  );

  const renderMapView = () => {
    return (
      <GoogleMapsView
        apartments={filteredApartments}
        userLocation={userLocation}
        searchLocation={searchLocation}
        onApartmentClick={handleApartmentClick}
        onLocationChange={handleLocationChange}
        height={400}
      />
    );
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Header Controls - Always show */}
      <Row 
        justify="space-between" 
        align="middle" 
        style={{ marginBottom: 24 }}
        gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]}
      >
        <Col xs={24} sm={12} md={8}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              value={searchType}
              onChange={setSearchType}
              style={{ width: '100%' }}
              placeholder="Select search type"
            >
              {searchOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            {searchType === 'Other' && (
              <Input
                placeholder="Enter custom search term"
                value={customSearchTerm}
                onChange={(e) => setCustomSearchTerm(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
              />
            )}
          </Space>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Space>
            <Button
              type={mapView === 'list' ? 'primary' : 'default'}
              onClick={() => setMapView('list')}
            >
              {t('sales:discovery.listView')}
            </Button>
            <Button
              type={mapView === 'map' ? 'primary' : 'default'}
              onClick={() => setMapView('map')}
            >
              {t('sales:discovery.mapView')}
            </Button>
            <Button
              type={mapView === 'split' ? 'primary' : 'default'}
              onClick={() => setMapView('split')}
            >
              {t('sales:discovery.splitView')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Prospect Tag Filtering Controls */}
      <Row 
        justify="start" 
        align="middle" 
        style={{ marginBottom: 16 }}
        gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]}
      >
        <Col xs={24} sm={24} md={24}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
              {t('sales:discovery.prospectTagging.filterProspects')}
            </Text>
            <Select
              value={filterType === 'by-tag' ? selectedTagFilter : filterType}
                          onChange={(value) => {
              if ([t('sales:discovery.prospectTagging.noWhatsApp'), t('sales:discovery.prospectTagging.alreadyContacted'), t('sales:discovery.prospectTagging.favourite')].includes(value)) {
                setFilterType('by-tag');
                setSelectedTagFilter(value as ProspectTagType);
              } else {
                setFilterType(value as 'all' | 'exclude-tagged');
                setSelectedTagFilter(null);
              }
            }}
              style={{ 
                width: window.innerWidth <= 768 ? '100%' : 200
              }}
              size={window.innerWidth <= 768 ? 'small' : 'middle'}
            >
              <Option value="exclude-tagged">{t('sales:discovery.prospectTagging.excludeTagged')}</Option>
              <Option value="all">{t('sales:discovery.prospectTagging.allPlaces')}</Option>
              <Option value={t('sales:discovery.prospectTagging.noWhatsApp')}>{t('sales:discovery.prospectTagging.noWhatsApp')}</Option>
              <Option value={t('sales:discovery.prospectTagging.alreadyContacted')}>{t('sales:discovery.prospectTagging.alreadyContacted')}</Option>
              <Option value={t('sales:discovery.prospectTagging.favourite')}>{t('sales:discovery.prospectTagging.favourite')}</Option>
            </Select>
          </Space>
        </Col>
      </Row>

      {/* Tag Summary */}
      <Row style={{ marginBottom: 16 }}>
        <Col xs={24} sm={24} md={24}>
          <Space 
            direction="vertical" 
            style={{ width: '100%' }}
          >
            <Text type="secondary" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
              {t('sales:discovery.prospectTagging.taggedPlaces')}
            </Text>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'row',
              gap: window.innerWidth <= 768 ? 4 : 8,
              width: '100%',
              overflowX: window.innerWidth <= 768 ? 'auto' : 'visible',
              flexWrap: window.innerWidth <= 768 ? 'nowrap' : 'wrap'
            }}>
              <Tag 
                color="red" 
                style={{ 
                  fontSize: window.innerWidth <= 768 ? '11px' : '12px',
                  padding: window.innerWidth <= 768 ? '2px 6px' : '4px 8px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {t('sales:discovery.prospectTagging.noWhatsApp')}: {prospectTags.filter(tag => tag.tagType === 'no-whatsapp').length}
              </Tag>
              <Tag 
                color="green"
                style={{ 
                  fontSize: window.innerWidth <= 768 ? '11px' : '12px',
                  padding: window.innerWidth <= 768 ? '2px 6px' : '4px 8px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {t('sales:discovery.prospectTagging.alreadyContacted')}: {prospectTags.filter(tag => tag.tagType === 'already-contacted').length}
              </Tag>
              <Tag 
                color="gold"
                style={{ 
                  fontSize: window.innerWidth <= 768 ? '11px' : '12px',
                  padding: window.innerWidth <= 768 ? '2px 6px' : '4px 8px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {t('sales:discovery.prospectTagging.favourite')}: {prospectTags.filter(tag => tag.tagType === 'favourite').length}
              </Tag>
              <Text 
                type="secondary" 
                style={{ 
                  fontSize: window.innerWidth <= 768 ? '11px' : '14px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Total: {prospectTags.length}
              </Text>
            </div>
          </Space>
        </Col>
      </Row>

      {/* Location Search Input - New line */}
      <Row style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card size="small" style={{ backgroundColor: '#fafafa' }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <AutoComplete
                  style={{ width: '100%' }}
                  placeholder="Start typing a city, address, or business name..."
                  value={manualLocation}
                  onChange={(value) => setManualLocation(value)}
                  onSearch={handleLocationSearch}
                  onSelect={handleLocationSelect}
                  options={locationSuggestions}
                  notFoundContent={isLoadingSuggestions ? <Spin size="small" /> : null}
                  allowClear
                  showSearch
                  filterOption={false}
                >
                  <Input
                    size="large"
                  />
                </AutoComplete>
              </Col>
              <Col flex="none">
                <Button
                  type="default"
                  icon={<AimOutlined />}
                  onClick={requestLocationPermission}
                  loading={isRequestingLocation}
                  size="large"
                  title="Use Current Location"
                  style={{
                    minWidth: 'auto',
                    padding: window.innerWidth <= 768 ? '8px 12px' : '8px 16px'
                  }}
                >
                  <span style={{ 
                    display: window.innerWidth <= 768 ? 'none' : 'inline',
                    marginLeft: window.innerWidth <= 768 ? 0 : 8
                  }}>
                    Current Location
                  </span>
                </Button>
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
              {t('sales:discovery.locationHint')}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Content - Always show split view, but conditionally show apartments */}
      {mapView === 'split' ? (
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            {renderMapView()}
          </Col>
          <Col xs={24} lg={12}>
            {searchLocation ? (
              <Card title={t('sales:discovery.nearbyApartments')}>
                {isLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                  </div>
                ) : error ? (
                  <Empty description={t('sales:discovery.errorLoading')} />
                ) : filteredApartments.length === 0 ? (
                  <Empty description="No apartments found in this area. Try increasing the search distance or check your location." />
                ) : (
                  <List
                    dataSource={filteredApartments}
                    renderItem={renderApartmentCard}
                    pagination={{
                      pageSize: 5,
                      showSizeChanger: false,
                    }}
                  />
                )}
              </Card>
            ) : (
              <Card title={t('sales:discovery.nearbyApartments')}>
                <Empty
                  image={<CompassOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                  description="Please set your location to discover nearby apartments"
                />
              </Card>
            )}
          </Col>
        </Row>
      ) : mapView === 'map' ? (
        renderMapView()
      ) : (
        searchLocation ? (
          <Card title={t('sales:discovery.nearbyApartments')}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
              </div>
            ) : error ? (
              <Empty description={t('sales:discovery.errorLoading')} />
            ) : filteredApartments.length === 0 ? (
              <Empty description="No apartments found in this area. Try increasing the search distance or check your location." />
            ) : (
              <List
                dataSource={filteredApartments}
                renderItem={renderApartmentCard}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                }}
              />
            )}
          </Card>
        ) : (
          <Card title={t('sales:discovery.nearbyApartments')}>
            <Empty
              image={<CompassOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
              description="Please set your location to discover nearby apartments"
            />
          </Card>
        )
      )}

      {/* Apartment Details Drawer */}
      <Drawer
        title={selectedApartment?.name}
        placement="right"
        width={400}
        onClose={() => setSelectedApartment(null)}
        open={!!selectedApartment}
      >
        {selectedApartment && (
          <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                             <div>
                 <Title level={5}>{t('sales:discovery.contactInfo')}</Title>
                 <Space direction="vertical">
                   {selectedApartment.phone && (
                     <Space>
                       <Text><PhoneOutlined /> {selectedApartment.phone}</Text>
                       <Dropdown
                         menu={{
                           items: getWhatsAppTemplates().map(template => ({
                             key: template.key,
                             label: template.label,
                             icon: <MessageOutlined />,
                             onClick: () => handleWhatsAppMessage(selectedApartment.phone!, template.key)
                           }))
                         }}
                         placement="bottomRight"
                         trigger={['click']}
                       >
                         <Button 
                           type="primary" 
                           size="small" 
                           icon={<MessageOutlined />}
                         >
                           Send WhatsApp
                         </Button>
                       </Dropdown>
                     </Space>
                   )}
                   {selectedApartment.nationalPhone && selectedApartment.nationalPhone !== selectedApartment.phone && (
                     <Space>
                       <Text><PhoneOutlined /> {selectedApartment.nationalPhone}</Text>
                       <Dropdown
                         menu={{
                           items: getWhatsAppTemplates().map(template => ({
                             key: template.key,
                             label: template.label,
                             icon: <MessageOutlined />,
                             onClick: () => handleWhatsAppMessage(selectedApartment.nationalPhone!, template.key)
                           }))
                         }}
                         placement="bottomRight"
                         trigger={['click']}
                       >
                         <Button 
                           type="primary" 
                           size="small" 
                           icon={<MessageOutlined />}
                         >
                           Send WhatsApp
                         </Button>
                       </Dropdown>
                     </Space>
                   )}
                   {selectedApartment.website && (
                     <Text><MailOutlined /> <a href={selectedApartment.website} target="_blank" rel="noopener noreferrer">{selectedApartment.website}</a></Text>
                   )}
                   <Text><EnvironmentOutlined /> {selectedApartment.address}</Text>
                 </Space>
               </div>
               
               <div>
                 <Title level={5}>{t('sales:discovery.details')}</Title>
                 <Space direction="vertical">
   
                   <Text>Rating: ⭐ {selectedApartment.rating || 'N/A'} ({selectedApartment.totalRatings || 0} reviews)</Text>
                   <Text>Place ID: {selectedApartment.placeId}</Text>
                 </Space>
               </div>

               {/* Prospect Tagging Section */}
               <div>
                 <Title level={5}>Prospect Management</Title>
                 <Space direction="vertical" style={{ width: '100%' }}>
                   {/* Current Tag Display */}
                   {prospectTags.find(tag => tag.placeId === selectedApartment.placeId) ? (
                     <div>
                       <Text strong>Current Tag:</Text>
                       <br />
                       <Tag
                         color={
                           prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'no-whatsapp' ? 'red' :
                           prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'already-contacted' ? 'green' :
                           prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'favourite' ? 'gold' : 'blue'
                         }
                         icon={
                           prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'no-whatsapp' ? <PhoneIcon /> :
                           prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'already-contacted' ? <CheckCircleOutlined /> :
                           prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'favourite' ? <StarOutlined /> : <TagOutlined />
                         }
                         style={{ fontSize: '14px', padding: '4px 8px' }}
                       >
                         {prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'no-whatsapp' ? t('sales:discovery.prospectTagging.noWhatsApp') :
                          prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'already-contacted' ? t('sales:discovery.prospectTagging.alreadyContacted') :
                          prospectTags.find(tag => tag.placeId === selectedApartment.placeId)?.tagType === 'favourite' ? t('sales:discovery.prospectTagging.favourite') : ''}
                       </Tag>
                     </div>
                   ) : (
                     <Text type="secondary">No tag assigned</Text>
                   )}
                   
                   {/* Tag Actions */}
                   <Space wrap>
                     <Dropdown
                       menu={{
                         items: [
                           // Add new tag options
                           {
                             key: 'no-whatsapp',
                             label: t('sales:discovery.prospectTagging.tagOptions.tagAs', { tagType: t('sales:discovery.prospectTagging.noWhatsApp') }),
                             icon: <PhoneIcon style={{ color: '#ff4d4f' }} />,
                             onClick: () => handleAddTag(selectedApartment, 'no-whatsapp')
                           },
                           {
                             key: 'already-contacted',
                             label: t('sales:discovery.prospectTagging.tagOptions.tagAs', { tagType: t('sales:discovery.prospectTagging.alreadyContacted') }),
                             icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                             onClick: () => handleAddTag(selectedApartment, 'already-contacted')
                           },
                           {
                             key: 'favourite',
                             label: t('sales:discovery.prospectTagging.tagOptions.tagAs', { tagType: t('sales:discovery.prospectTagging.favourite') }),
                             icon: <StarOutlined style={{ color: '#faad14' }} />,
                             onClick: () => handleAddTag(selectedApartment, 'favourite')
                           },
                           // Change tag options if current tag exists
                           ...(prospectTags.find(tag => tag.placeId === selectedApartment.placeId) ? [
                             { type: 'divider' as const },
                             {
                               key: 'change-to-no-whatsapp',
                               label: t('sales:discovery.prospectTagging.tagOptions.changeTo', { tagType: t('sales:discovery.prospectTagging.noWhatsApp') }),
                               icon: <PhoneIcon style={{ color: '#ff4d4f' }} />,
                               onClick: () => handleUpdateTag(selectedApartment.placeId, 'no-whatsapp')
                             },
                             {
                               key: 'change-to-already-contacted',
                               label: t('sales:discovery.prospectTagging.tagOptions.changeTo', { tagType: t('sales:discovery.prospectTagging.alreadyContacted') }),
                               icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                               onClick: () => handleUpdateTag(selectedApartment.placeId, 'already-contacted')
                             },
                             {
                               key: 'change-to-favourite',
                               label: t('sales:discovery.prospectTagging.tagOptions.changeTo', { tagType: t('sales:discovery.prospectTagging.favourite') }),
                               icon: <StarOutlined style={{ color: '#faad14' }} />,
                               onClick: () => handleUpdateTag(selectedApartment.placeId, 'favourite')
                             },
                             { type: 'divider' as const },
                             {
                               key: 'remove-tag',
                               label: t('sales:discovery.prospectTagging.tagOptions.removeTag'),
                               icon: <TagOutlined />,
                               onClick: () => handleRemoveTag(selectedApartment.placeId)
                             }
                           ] : [])
                         ]
                       }}
                       placement="bottomRight"
                       trigger={['click']}
                     >
                       <Button
                         icon={<TagOutlined />}
                         style={{
                           color: prospectTags.find(tag => tag.placeId === selectedApartment.placeId) ? '#1890ff' : undefined
                         }}
                       >
                         {prospectTags.find(tag => tag.placeId === selectedApartment.placeId) ? t('sales:discovery.prospectTagging.tagOptions.manageTag') : t('sales:discovery.prospectTagging.tagOptions.addTag')}
                       </Button>
                     </Dropdown>
                   </Space>
                 </Space>
               </div>

               {selectedApartment.photos && selectedApartment.photos.length > 0 && (
                 <div>
                   <Title level={5}>Photos</Title>
                   <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                     {selectedApartment.photos.map((photo, index: number) => (
                       <img 
                         key={index} 
                         src={`https://places.googleapis.com/v1/${photo.name}/media?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&maxWidthPx=120&maxHeightPx=90`}
                         alt={`${selectedApartment.name} photo ${index + 1}`}
                         style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 4 }}
                         onError={(e) => {
                           e.currentTarget.style.display = 'none';
                         }}
                       />
                     ))}
                   </div>
                 </div>
               )}

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleCreateLead(selectedApartment)}
                style={{ width: '100%' }}
              >
                {t('sales:discovery.createLead')}
              </Button>
            </Space>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default DiscoveryTab; 